import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { httpRequest } from "../../utils/httpRequest";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import CustomEditor from "../common/Editor";
import ExplanationField from "./ExplanationField";

const makeEmptyForm = () => ({
  stem_fa: "",
  stem_en: "",
  layout: "top-bottom",
  tokens: [
    { key: "t1", fa: "", en: "", inSeq: true, order: 1 },
    { key: "t2", fa: "", en: "", inSeq: true, order: 2 },
    { key: "t3", fa: "", en: "", inSeq: false, order: 0 },
  ],
  points: 1,
  status: "published",
  tags: "",
  explanation_fa: "",
  explanation_en: "",
});

const itemToForm = (item) => {
  if (!item) return makeEmptyForm();

  const stem_fa = item?.stem?.fa || "";
  const stem_en = item?.stem?.en || "";
  const body = item?.body || {};
  const layout = body.layout || "top-bottom";

  const defaultForm = makeEmptyForm();

  const tokensBody =
    Array.isArray(body.tokens) && body.tokens.length ? body.tokens : null;

  let tokens = defaultForm.tokens;

  const explanation_fa = item?.explanation?.fa || "";
  const explanation_en = item?.explanation?.en || "";

  if (tokensBody) {
    const firstCorrectSeq =
      Array.isArray(body.correct) && body.correct.length > 0
        ? body.correct[0]
        : null;

    const correctSeq =
      Array.isArray(firstCorrectSeq) && firstCorrectSeq.length > 0
        ? firstCorrectSeq
        : null;

    tokens = tokensBody.map((tk, idx) => {
      const key = tk.key || `t${idx + 1}`;
      const fa = tk.label?.fa || "";
      const en = tk.label?.en || "";

      let inSeq = false;
      let order = 0;

      if (correctSeq) {
        const pos = correctSeq.indexOf(key);
        if (pos !== -1) {
          inSeq = true;
          order = pos + 1;
        }
      }

      return {
        key,
        fa,
        en,
        inSeq,
        order,
      };
    });
  }

  return {
    stem_fa,
    stem_en,
    layout,
    tokens,
    points:
      typeof item.points === "number" && !Number.isNaN(item.points)
        ? item.points
        : 1,
    status: item.status || "published",
    tags: Array.isArray(item.tags) ? item.tags.join(",") : "",
    explanation_fa,
    explanation_en,
  };
};

export default function SentenceBuilderModal({
  isOpen,
  onClose,
  token,
  onCreated,
  mode = "create", // "create" | "edit"
  item = null,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");
  const isEdit = mode === "edit" && item;

  const [form, setForm] = useState(makeEmptyForm());

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) {
      setForm(itemToForm(item));
    } else {
      setForm(makeEmptyForm());
    }
  }, [isOpen, isEdit, item]);

  if (!isOpen) return null;

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const addToken = () =>
    setForm((f) => ({
      ...f,
      tokens: [
        ...f.tokens,
        {
          key: `t${f.tokens.length + 1}`,
          fa: "",
          en: "",
          inSeq: false,
          order: 0,
        },
      ],
    }));

  const removeToken = (idx) =>
    setForm((f) => ({ ...f, tokens: f.tokens.filter((_, i) => i !== idx) }));

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= form.tokens.length) return;
    const next = [...form.tokens];
    const [it] = next.splice(idx, 1);
    next.splice(j, 0, it);
    setForm((f) => ({ ...f, tokens: next }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const used = form.tokens.filter((tk) => tk.inSeq);
    if (used.length < 2) {
      toast.error(t("sentence_builder.toasts.need_two_tokens"));
      return;
    }

    const correct = used
      .slice()
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
      .map((tk) => tk.key);

    if (correct.some((k) => !k)) {
      toast.error(t("sentence_builder.toasts.keys_required"));
      return;
    }

    const payload = {
      kind: "sentence_builder",
      explanation: {
        fa: form.explanation_fa || undefined,
        en: form.explanation_en || undefined,
      },
      stem: {
        fa: form.stem_fa || undefined,
        en: form.stem_en || undefined,
      },
      body: {
        layout: form.layout,
        tokens: form.tokens.map((tk) => ({
          key: tk.key,
          label: { fa: tk.fa || undefined, en: tk.en || undefined },
        })),
        correct: [correct],
      },
      points: Number(form.points) || 1,
      status: form.status || "draft",
      tags: form.tags
        ? form.tags
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };

    try {
      const isEditMode = isEdit && item?._id;
      const url = isEditMode ? `/item/${item._id}` : "/item";
      const method = isEditMode ? "PUT" : "POST";

      const res = await httpRequest(method, url, payload, {
        "x-access-token": token,
      });
      const doc = res?.data?.data || res?.data;
      if (!doc?._id) throw new Error(res?.message || "FAILED");

      toast.success(
        isEditMode
          ? t("sentence_builder.toasts.updated", "آیتم با موفقیت ویرایش شد")
          : t("sentence_builder.toasts.created")
      );
      onCreated?.(doc);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Stem */}{" "}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {" "}
        <div dir={isRtl ? "rtl" : "ltr"}>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("sentence_builder.stem_fa")}
          </label>{" "}
          <CustomEditor
            value={form.stem_fa}
            onEditorChange={(content) => handleEditorChange("stem_fa", content)}
            placeholder={t("sentence_builder.stem_fa_ph")}
            directionality={isRtl ? "rtl" : "ltr"}
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />{" "}
        </div>{" "}
        <div dir="ltr">
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("sentence_builder.stem_en")}
          </label>{" "}
          <CustomEditor
            value={form.stem_en}
            onEditorChange={(content) => handleEditorChange("stem_en", content)}
            placeholder={t("sentence_builder.stem_en_ph")}
            directionality="ltr"
            height={150}
            toolbar="undo redo | bold italic underline | bullist numlist"
          />{" "}
        </div>{" "}
      </div>
      {/* Layout Selector */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {t("sentence_builder.layout_label")}
        </label>
        <div className="flex items-center flex-wrap gap-4 border p-3 rounded-lg bg-gray-50">
          {["top-bottom", "bottom-top", "left-right", "right-left"].map(
            (layout) => (
              <div key={layout} className="flex items-center">
                <input
                  type="radio"
                  id={`layout-${layout}`}
                  name="layout"
                  value={layout}
                  checked={form.layout === layout}
                  onChange={onChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                />
                <label
                  htmlFor={`layout-${layout}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {t(`sentence_builder.layout_question_answer_${layout}`)}
                </label>
              </div>
            )
          )}
        </div>
      </div>{" "}
      <div>
        {" "}
        <div className="flex items-center justify-between mb-1">
          {" "}
          <label className="block text-sm font-medium">
            {t("sentence_builder.tokens")}
          </label>{" "}
          <button
            type="button"
            onClick={addToken}
            className="text-blue-600 text-sm"
          >
            <Plus size={16} className="inline-block mr-1" />{" "}
            {t("sentence_builder.add_token")}{" "}
          </button>{" "}
        </div>{" "}
        <div className="space-y-2">
          {" "}
          {form.tokens.map((tk, idx) => (
            <div key={tk.key} className="border rounded-lg p-3">
              {" "}
              <div className="flex items-center justify-between mb-2">
                {" "}
                <span className="text-xs font-semibold">{tk.key}</span>{" "}
                <div className="flex items-center gap-3">
                  {" "}
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                  >
                    <ArrowUp size={16} />{" "}
                  </button>{" "}
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    onClick={() => move(idx, +1)}
                    disabled={idx === form.tokens.length - 1}
                  >
                    <ArrowDown size={16} />{" "}
                  </button>{" "}
                  {form.tokens.length > 2 && (
                    <button
                      type="button"
                      className="p-1 rounded-full text-red-600 hover:bg-red-100"
                      onClick={() => removeToken(idx)}
                    >
                      <Trash2 size={16} />{" "}
                    </button>
                  )}{" "}
                </div>{" "}
              </div>{" "}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                {" "}
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("sentence_builder.token_fa")}
                  value={tk.fa}
                  onChange={(e) => {
                    const tokens = [...form.tokens];
                    tokens[idx].fa = e.target.value;
                    setForm((f) => ({ ...f, tokens }));
                  }}
                />{" "}
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("sentence_builder.token_en")}
                  value={tk.en}
                  onChange={(e) => {
                    const tokens = [...form.tokens];
                    tokens[idx].en = e.target.value;
                    setForm((f) => ({ ...f, tokens }));
                  }}
                />{" "}
                <div className="flex items-center gap-2">
                  {" "}
                  <input
                    type="checkbox"
                    id={`inSeq-${idx}`}
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    checked={tk.inSeq}
                    onChange={(e) => {
                      const tokens = [...form.tokens];
                      tokens[idx].inSeq = e.target.checked;
                      setForm((f) => ({ ...f, tokens }));
                    }}
                  />{" "}
                  <label
                    htmlFor={`inSeq-${idx}`}
                    className="ml-2 text-sm text-gray-700"
                  >
                    {t("sentence_builder.use_in_answer")}
                  </label>{" "}
                </div>{" "}
                <input
                  type="number"
                  min={0}
                  className="border rounded-lg px-3 py-2"
                  placeholder={t("sentence_builder.order_ph")}
                  value={tk.order}
                  onChange={(e) => {
                    const tokens = [...form.tokens];
                    tokens[idx].order = e.target.value;
                    setForm((f) => ({ ...f, tokens }));
                  }}
                />{" "}
              </div>{" "}
            </div>
          ))}{" "}
        </div>{" "}
      </div>{" "}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("sentence_builder.points")}
          </label>{" "}
          <input
            type="number"
            min={0}
            className="w-full border rounded-lg px-3 py-2"
            value={form.points}
            onChange={(e) => setForm((f) => ({ ...f, points: e.target.value }))}
          />{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("sentence_builder.status")}
          </label>{" "}
          <select
            className="w-full border rounded-lg px-3 py-2"
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          >
            {" "}
            <option value="published">{t("common.published")}</option>
            <option value="draft">{t("common.draft")}</option>{" "}
          </select>{" "}
        </div>{" "}
        <div>
          {" "}
          <label className="block text-sm font-medium mb-1">
            {t("sentence_builder.tags")}
          </label>{" "}
          <input
            className="w-full border rounded-lg px-3 py-2"
            placeholder={t("common.tags_csv")}
            value={form.tags}
            onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
          />{" "}
        </div>{" "}
      </div>{" "}
      <ExplanationField
        valueFa={form.explanation_fa}
        valueEn={form.explanation_en}
        onChangeFa={(v) => handleEditorChange("explanation_fa", v)}
        onChangeEn={(v) => handleEditorChange("explanation_en", v)}
        isRtl={isRtl}
      />
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded-lg"
      >
        {isEdit ? t("common.update") : t("common.create")}
      </button>{" "}
    </form>
  );
}
